import { useState, useEffect } from 'react';
import type { LocationResult } from '../components/search/LocationSearch';
import { readPersisted, removePersisted, writePersisted } from '../utils/persistence';

const RECENT_SEARCHES_KEY = 'weatherapp-recent-searches';
const MAX_RECENT_SEARCHES = 5;

export interface RecentSearch extends LocationResult {
  searchedAt: string;
}

export function useLocationSearch() {
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);

  // Load recent searches from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) {
        setRecentSearches(readPersisted<RecentSearch[]>(RECENT_SEARCHES_KEY, []));
      }
    } catch (error) {
      console.error('Error loading recent searches:', error);
    }
  }, []);

  // Save a search to recent searches
  const saveSearch = (location: LocationResult) => {
    const newSearch: RecentSearch = {
      ...location,
      searchedAt: new Date().toISOString(),
    };

    setRecentSearches((prev) => {
      // Remove duplicate if it exists
      const filtered = prev.filter((s) => s.id !== location.id);
      
      // Add new search at the beginning
      const updated = [newSearch, ...filtered].slice(0, MAX_RECENT_SEARCHES);
      
      // Save to localStorage
      writePersisted(RECENT_SEARCHES_KEY, updated);
      
      return updated;
    });
  };

  // Clear all recent searches
  const clearSearches = () => {
    setRecentSearches([]);
    removePersisted(RECENT_SEARCHES_KEY);
  };

  // Remove a specific search
  const removeSearch = (id: number) => {
    setRecentSearches((prev) => {
      const updated = prev.filter((s) => s.id !== id);
      
      writePersisted(RECENT_SEARCHES_KEY, updated);
      
      return updated;
    });
  };

  return {
    recentSearches,
    saveSearch,
    clearSearches,
    removeSearch,
  };
}
